import { cn } from '@crowdex/ui/lib/utils';
import { SquareUserRound } from 'lucide-react';
import { personaTone } from './assistant-model';

/** A persona as its mark draws it: its name, and its avatar when it has one. */
export type PersonaMarkPersona = { name: string; avatar?: string | undefined };

const SIZES = {
  xs: 'size-4 text-micro',
  sm: 'size-5 text-micro',
  md: 'size-7 text-xs',
  lg: 'size-8 text-xs',
} as const;

/**
 * The letters of a persona's mark: its first, and when another persona shown starts with the
 * same one, the first of its letters that tells it from them ("Developer" and "Designer": "Dv"
 * and "Ds"), so two marks side by side never read the same (L2).
 */
export function personaLetters(name: string, shown: readonly string[] = []): string {
  const own = name.trim();
  const first = own[0]?.toUpperCase() ?? '?';
  const rivals = shown
    .map((other) => other.trim())
    .filter((other) => other !== own && other[0]?.toUpperCase() === first)
    .map((other) => other.toLowerCase());
  if (rivals.length === 0) return first;
  const lower = own.toLowerCase();
  for (let index = 1; index < lower.length; index += 1) {
    const letter = lower[index];
    if (letter === undefined || !/\p{L}|\p{N}/u.test(letter)) continue;
    if (rivals.every((other) => other[index] !== letter)) return `${first}${letter}`;
  }
  return first;
}

/**
 * A persona's mark, everywhere a persona shows (projects, @ mentions, Settings › Personas): its
 * avatar image, else its glyph and first letter on its colour (its place among the personas
 * shown), never bare initials ("D" and "Ds" side by side). Too small for the glyph, the letter
 * on its colour. Decorative: the persona's name is said beside it.
 */
export function PersonaMark({
  persona,
  index,
  shown,
  size = 'lg',
  className,
}: {
  persona: PersonaMarkPersona;
  index: number;
  /** The names of the personas shown with it: a shared first letter takes a second one. */
  shown?: readonly string[] | undefined;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const letter = personaLetters(persona.name, shown);
  if (persona.avatar !== undefined)
    return (
      <img
        alt=""
        className={cn('shrink-0 rounded-full object-cover', SIZES[size], className)}
        data-persona-mark="avatar"
        src={persona.avatar}
      />
    );
  const small = size === 'xs' || size === 'sm';
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-foreground',
        SIZES[size],
        personaTone(index),
        className,
      )}
      data-persona-mark="glyph"
    >
      {small ? (
        letter
      ) : (
        <>
          <SquareUserRound className={size === 'md' ? 'size-3.5' : 'size-4'} />
          <span className="absolute -right-0.5 -bottom-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-card px-px text-micro">
            {letter}
          </span>
        </>
      )}
    </span>
  );
}
